import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, Calendar, Clock, Car, ImageOff } from "lucide-react";
import { Vehicle } from "../../types";
import VehicleCard from "../Home/components/VehicleCard/VehicleCard";
import { Button } from "../../components/ui";
import { handleEvaluationButtonClick } from "../../utils/redirectToNewReview";

type ArticleBrand = {
  id: number;
  title: string;
  slug: string;
};

// Article de la sidebar « Articles récents » — sous-ensemble de la vue :card.
type RecentArticle = {
  title: string;
  slug: string;
  featured_image_url: string | null;
  published_at: string;
};

type BlogShowProps = {
  // 3 articles publiés les plus récents (hors courant) — sidebar de droite.
  recent_articles: RecentArticle[];
  article: {
    title: string;
    slug: string;
    body: string;
    meta_title: string | null;
    meta_description: string | null;
    canonical_url: string;
    date_published: string;
    date_modified: string;
    featured_image_url: string | null;
    author_name: string;
    publisher_name: string;
    publisher_logo_url: string;
    // Temps de lecture serveur (minutes, min 1) — vue :show (plan 01, D-14).
    reading_time: number;
    tags: string[];
    // Bloc véhicules à découvrir (forme VehicleSerializer vue :card, D-08).
    vehicles: Vehicle[];
    brands: ArticleBrand[];
  };
};

// Format FR jour mois année — rendu déterministe pour le SSR (miroir de Blog/Index).
const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

// Échappement anti-XSS du JSON injecté dans <script> (T-04-XSS / CR-01) : un titre
// admin contenant `</script>` casserait le tag rendu en SSR. Les séquences \u00XX
// restent du JSON valide — pattern identique au htmlEscapeJsonString de Next.js.
const JSON_LD_ESCAPE: Record<string, string> = {
  "&": "\\u0026",
  "<": "\\u003c",
  ">": "\\u003e",
  "\u2028": "\\u2028",
  "\u2029": "\\u2029",
};

const escapeJsonForScript = (json: string) =>
  json.replace(/[&<>\u2028\u2029]/g, (match) => JSON_LD_ESCAPE[match]);

// Sidebar \u00ab Articles récents \u00bb \u2014 colonne de droite de la vue d\u00e9tail (maquette
// blog.jsx RecentArticles). Vignette 80\u00d780 + titre (3 lignes max) + date ;
// collante au scroll en desktop. Les items sont des <Link> crawlables (D-06).
const RecentArticles = ({ articles }: { articles: RecentArticle[] }) => (
  <aside className="lg:sticky lg:top-24 self-start">
    <span className="text-2xl uppercase tracking-wide mb-4 font-semibold">
      Articles récents
    </span>
    <div className="flex flex-col gap-4">
      {articles.map((a) => (
        <Link
          key={a.slug}
          href={`/blog/articles/${a.slug}`}
          className="group flex gap-3 text-left no-underline text-inherit"
        >
          {a.featured_image_url ? (
            <img
              src={a.featured_image_url}
              alt=""
              loading="lazy"
              className="w-20 h-20 shrink-0 rounded-lg object-cover bg-gray-100"
            />
          ) : (
            <div
              className="w-20 h-20 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400"
              aria-hidden="true"
            >
              <ImageOff className="w-6 h-6" strokeWidth={1.5} />
            </div>
          )}
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="text-sm font-semibold leading-snug line-clamp-3 group-hover:text-primary transition-colors">
              {a.title}
            </h3>
            <span className="text-xs text-muted-foreground">
              {formatDate(a.published_at)}
            </span>
          </div>
        </Link>
      ))}
    </div>
    <Link
      href="/blog"
      className="mt-5 inline-block text-sm font-semibold text-primary hover:underline"
    >
      Voir tous les articles →
    </Link>
  </aside>
);

const BlogShow = ({ article, recent_articles }: BlogShowProps) => {
  // JSON-LD BlogPosting (BLOG-09) — sérialisé en string (anti-pattern : pas de JSX enfant brut).
  const jsonLd = escapeJsonForScript(
    JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      image: article.featured_image_url,
      datePublished: article.date_published,
      dateModified: article.date_modified,
      author: {
        "@type": "Person",
        name: article.author_name,
      },
      publisher: {
        "@type": "Organization",
        name: article.publisher_name,
        logo: {
          "@type": "ImageObject",
          url: article.publisher_logo_url,
        },
      },
    }),
  );

  const metaTitle = article.meta_title || article.title;
  const metaDescription = article.meta_description || "";

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={article.canonical_url} />

        {/* OpenGraph (BLOG-11) */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {article.featured_image_url && (
          <meta property="og:image" content={article.featured_image_url} />
        )}
        <meta property="og:url" content={article.canonical_url} />

        {/* Twitter (BLOG-11) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {article.featured_image_url && (
          <meta name="twitter:image" content={article.featured_image_url} />
        )}

        {/* JSON-LD BlogPosting (BLOG-09) */}
        <script type="application/ld+json">{jsonLd}</script>
      </Head>

      {/* Vue détail (maquette blog.jsx ArticlePage) — zone élargie à 1180px,
          grille 2 colonnes : corps de l'article + sidebar « Articles récents ». */}
      <div className="max-w-[1180px] mx-auto px-4 my-8 lg:my-12">
        <Link
          href="/blog"
          className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au blog
        </Link>

        {/* 2 colonnes en desktop (corps + sidebar 300px) ; 1 colonne si aucun
            article récent à afficher (pas de colonne vide). */}
        <div
          className={
            recent_articles.length > 0
              ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-10 lg:gap-14 mt-5"
              : "mt-5"
          }
        >
          <article className="min-w-0">
            {article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-[#EFECFF] text-[#412FB2] rounded-md px-2.5 py-1 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <h1 className="text-3xl lg:text-5xl font-bold mt-3 leading-tight text-balance">
              {article.title}
            </h1>

            {/* Méta : date • temps de lecture (D-14). */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(article.date_published)}</span>
              <span className="text-gray-300">•</span>
              <Clock className="w-4 h-4" />
              <span>{article.reading_time} min de lecture</span>
            </div>

            {/* Couverture — hero réduit (ratio 0,75 : 315/285px desktop, 225px
                mobile) avec marge basse mb-6 (maquette). */}
            {article.featured_image_url && (
              <img
                src={article.featured_image_url}
                alt={article.title}
                className="w-full h-[225px] lg:h-[315px] object-cover rounded-xl mt-6 mb-6 bg-gray-100"
              />
            )}

            {/* Corps via la CSS partagée .article-body (D-09/D-10) — sanitizé côté Rails (plan 01). */}
            <div
              className="article-body"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />

            {/* Bloc véhicules à découvrir (D-08) — masqué si aucun véhicule. */}
            {article.vehicles.length > 0 && (
              <section className="mt-14">
                <div className="flex flex-row items-center gap-2">
                  <i className="rounded-full p-2 bg-primary inline-flex">
                    <Car className="w-6 h-6 text-white" />
                  </i>
                  <span className="text-lg lg:text-2xl font-medium uppercase">
                    Véhicules à découvrir
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
                  {article.vehicles.map((vehicle) => (
                    <VehicleCard
                      key={vehicle.id}
                      vehicle={vehicle}
                      showCompare={false}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* CTA final indigo (D-04) — cohérent avec le bloc « partager son expérience » de la home. */}
            <div className="mt-12 rounded-2xl bg-[#412FB2] text-white text-center px-6 py-10">
              <h3 className="text-2xl font-bold mb-2">
                Vous aussi, partagez votre expérience
              </h3>
              <p className="text-white/80 mb-6 text-[15px]">
                Votre avis aide les autres conducteurs à faire le bon choix. Et
                ne prend que 2 minutes.
              </p>
              <Button
                variant="white"
                size="lg"
                onClick={() =>
                  handleEvaluationButtonClick({ page_type: "Article de blog" })
                }
              >
                Évaluer mon véhicule
              </Button>
            </div>
          </article>

          {recent_articles.length > 0 && (
            <RecentArticles articles={recent_articles} />
          )}
        </div>
      </div>
    </>
  );
};

export default BlogShow;
